import React, { useState, useMemo } from 'react'
import {
	EuiButtonIcon,
	EuiContextMenuItem,
	EuiContextMenuPanel,
	EuiPopover,
	EuiConfirmModal,
} from '@equipmentshare/ds2'
import { DemandRequestStatusData, canUserCancelDemandRequest } from '../../utils/demandRequestStatus'
import { useDemandRequestActions } from '../../hooks/useDemandRequestActions'
import { useAuthenticatedUser } from '../../../../hooks/useAuthenticatedUser'
import { useDemandRequestStores } from '../../hooks/useDemandRequestStores'

export const ACTION_KEYS = {
	REOPEN: 'reopen',
	ASSIGN_TO_ME: 'assign-to-me',
	UNASSIGN_FROM_ME: 'unassign-from-me',
	MARK_AS_COMPLETED: 'mark-as-completed',
	CANCEL: 'cancel',
} as const

export type RequestActionsMenuProps = {
	request: DemandRequestStatusData & {
		id: string,
		// The location whose permissions govern working this request
		owningInventory?: { id: string } | null,
		requestedByUser?: { id: string } | null,
		dateCancelled?: string | null,
	},
	canUserEditRequests: boolean,
	onActionComplete?: () => void,
	onMarkAsCompleted?: () => Promise<void>,
}

export default function RequestActionsMenu( {
	request,
	canUserEditRequests,
	onActionComplete,
	onMarkAsCompleted,
}: RequestActionsMenuProps ) {
	const authenticatedUser = useAuthenticatedUser()
	const { stores: storesWithEditPermission } = useDemandRequestStores()
	const {
		assignToMe,
		unassignFromMe,
		markAsCompleted,
		reopen,
		cancel,
		isAssigningToMe,
		isUnassigningFromMe,
		isMarkingAsCompleted,
		isReopening,
		isCancelling,
	} = useDemandRequestActions()

	const [ isPopoverOpen, setIsPopoverOpen ] = useState( false )
	const [ showUnassignModal, setShowUnassignModal ] = useState( false )

	const togglePopover = () => setIsPopoverOpen( ( prev ) => !prev )
	const closePopover = () => setIsPopoverOpen( false )

	const isAssignedToMe = request.assignedToUser?.id === authenticatedUser.id
	const isCompleted = !!request.dateCompleted
	const isCancelled = !!request.dateCancelled

	// Check if user can cancel this request
	const canCancel = useMemo( () => {
		if ( !request.owningInventory?.id || !authenticatedUser.id ) {
			return false
		}

		return canUserCancelDemandRequest( {
			owningInventoryId: request.owningInventory.id,
			requestingUserId: request.requestedByUser?.id,
			assignedToUserId: request.assignedToUser?.id,
			authenticatedUserId: authenticatedUser.id,
			storesWithEditPermission: storesWithEditPermission.map( ( s ) => s.id ),
		} )
	}, [
		request.owningInventory?.id,
		request.requestedByUser?.id,
		request.assignedToUser?.id,
		authenticatedUser.id,
		storesWithEditPermission,
	] )

	const handleAssignToMe = async() => {
		closePopover()
		await assignToMe( request.id )
		onActionComplete?.()
	}

	const handleUnassignClick = () => {
		closePopover()
		setShowUnassignModal( true )
	}

	const handleUnassignConfirm = async() => {
		setShowUnassignModal( false )
		await unassignFromMe( request.id )
		onActionComplete?.()
	}

	const handleUnassignCancel = () => {
		setShowUnassignModal( false )
	}

	const handleMarkAsCompleted = async() => {
		closePopover()
		if ( onMarkAsCompleted ) {
			await onMarkAsCompleted()
		} else {
			await markAsCompleted( request.id )
		}
		onActionComplete?.()
	}

	const handleReopen = async() => {
		closePopover()
		await reopen( request.id )
		onActionComplete?.()
	}

	const handleCancel = async() => {
		closePopover()
		await cancel( request.id )
		onActionComplete?.()
	}

	const menuItems = []

	// If completed or cancelled, only show "Reopen" action
	if ( canUserEditRequests && ( isCompleted || isCancelled ) ) {
		menuItems.push(
			<EuiContextMenuItem
				key={ACTION_KEYS.REOPEN}
				icon="refresh"
				onClick={handleReopen}
				disabled={isReopening}
			>
				{isReopening ? 'Reopening...' : 'Reopen'}
			</EuiContextMenuItem>
		)
	}

	// Show "Assign to Me" if not assigned to current user and not completed or cancelled
	if ( canUserEditRequests && !isAssignedToMe && !isCompleted && !isCancelled ) {
		menuItems.push(
			<EuiContextMenuItem
				key={ACTION_KEYS.ASSIGN_TO_ME}
				icon="userAvatar"
				onClick={handleAssignToMe}
				disabled={isAssigningToMe}
			>
				{isAssigningToMe ? 'Assigning...' : 'Assign to Me'}
			</EuiContextMenuItem>
		)
	}

	// Show "Unassign from Me" if assigned to current user and not completed or cancelled
	if ( isAssignedToMe && !isCompleted && !isCancelled ) {
		menuItems.push(
			<EuiContextMenuItem
				key={ACTION_KEYS.UNASSIGN_FROM_ME}
				icon="cross"
				onClick={handleUnassignClick}
				disabled={isUnassigningFromMe}
			>
				{isUnassigningFromMe ? 'Unassigning...' : 'Unassign from Me'}
			</EuiContextMenuItem>
		)
	}

	// Show "Mark as Completed" if not completed or cancelled
	if ( canUserEditRequests && !isCompleted && !isCancelled ) {
		menuItems.push(
			<EuiContextMenuItem
				key={ACTION_KEYS.MARK_AS_COMPLETED}
				icon="flag"
				onClick={handleMarkAsCompleted}
				disabled={isMarkingAsCompleted}
			>
				{isMarkingAsCompleted ? 'Marking as Completed...' : 'Mark as Completed'}
			</EuiContextMenuItem>
		)
	}

	// Show "Cancel" if user has permission and request is not completed or cancelled
	if ( canCancel && !isCompleted && !isCancelled ) {
		menuItems.push(
			<EuiContextMenuItem
				key={ACTION_KEYS.CANCEL}
				icon="cross"
				onClick={handleCancel}
				disabled={isCancelling}
				color="danger"
			>
				{isCancelling ? 'Cancelling...' : 'Cancel Request'}
			</EuiContextMenuItem>
		)
	}

	// If no actions available, don't render the menu
	if ( menuItems.length === 0 ) {
		return null
	}

	return (
		<>
			<EuiPopover
				button={
					<EuiButtonIcon
						iconType="boxesHorizontal"
						aria-label="Actions"
						color="text"
						onClick={togglePopover}
					/>
				}
				isOpen={isPopoverOpen}
				closePopover={closePopover}
				panelPaddingSize="none"
				anchorPosition="downLeft"
			>
				<EuiContextMenuPanel items={menuItems} />
			</EuiPopover>

			{showUnassignModal && (
				<EuiConfirmModal
					title="Unassign Request"
					onCancel={handleUnassignCancel}
					onConfirm={handleUnassignConfirm}
					cancelButtonText="Cancel"
					confirmButtonText="Unassign"
					buttonColor="primary"
				>
					<p>Are you sure you want to unassign yourself from this request?</p>
				</EuiConfirmModal>
			)}
		</>
	)
}
